import { useMutation, useQuery } from '@apollo/client';
import { EmptyState } from '@components/DataTable';
import CloseIcon from '@components/Icon/Close';
import { FlexRow } from '@components/FlexRow';
import { SearchBar } from '@components/SearchBar';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_JOB_OFFER_UPERCUT, SEND_JOB_INVITATION_UPERCUT } from '@gql/jobs';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Stack, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useState } from 'react';

import JobOfferCard from './JobOfferCard';
import JobOfferCardSkeleton from './JobOfferCardSkeleton';

interface SuggestJobOfferDialogProps {
    open: boolean;
    setOpen: (prev: boolean) => void;
    talentIds: string[]; // Array of talent IDs to suggest the job offer to
    refetchQueries?: any[]
}

const SuggestJobOfferDialog = ({ open, setOpen, talentIds, refetchQueries }: SuggestJobOfferDialogProps) => {

    const [sendJobInvitation, { loading: isLoadingSubmit }] = useMutation(SEND_JOB_INVITATION_UPERCUT);
    const { showSnackbar } = useSnackbar();

    const onSubmit = async (jobNumbers: string[]) => {
        try {
            await sendJobInvitation({
                variables: {
                    jobNumbers: jobNumbers,
                    talentIds: talentIds,
                },
                refetchQueries,
            });
            // setSnackbarAction({
            //     show: true,
            //     message: 'Invitation has been sent',
            //     status: 'success',
            // });
            showSnackbar('L\'invitation a été envoyée avec succès.', 'success');
            handleClose();
        } catch (error) {
            // setSnackbarAction({
            //     show: true,
            //     message: error.message,
            //     status: 'error',
            // });
            showSnackbar(`Erreur lors de l'envoi de l'invitation : ${error.message}`, 'error');
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [search, setSearch] = useState<string>('');
    const [selectedValues, setSelectedValues] = useState<string[]>([]);

    const handleClose = () => {
        if (isLoadingSubmit) return;
        setOpen(false);
        setSelectedValues([]);
        setSearch('');
    };

    const {
        loading,
        // error,
        data: dataListJobOffers,
        // refetch,
    } = useQuery(GET_JOB_OFFER_UPERCUT, {
        notifyOnNetworkStatusChange: true,
        variables: {
            query: search,
            status: 'active',
            pagination: {
                page: 1,
                limit: 6,
            },
            sortBy: 'publishDate',
            filter: {},
        },
    });

    const data = dataListJobOffers?.getJobOffersUpercut?.docs || [];

    const handleSubmit = () => {
        onSubmit(selectedValues);
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" disableEscapeKeyDown={isLoadingSubmit}>
            <Stack direction="row" justifyContent="space-between" alignItems="start" sx={{ mb: 2, }}>
                <DialogTitle sx={{ flex: 1, mb: 0, fontSize: '28px', fontWeight: 600, }}>
                    Veuillez sélectionner l'offre d'emploi que vous souhaitez recommander pour {talentIds.length > 1 ? 'ces talents' : 'ce talent'}
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    disabled={isLoadingSubmit}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Stack>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <FlexRow className="search" sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    backgroundColor: theme => theme.palette.background.paper,
                    pb: 1,
                }}>
                    <SearchBar onChange={setSearch} fullWidth />
                    {/* Number of Selected */}
                    <Box sx={{
                        backgroundColor: theme => `${theme.palette.primary.main}4D`, // 4D = 30% opacity in hex
                        borderRadius: '16px',
                    }}>
                        <Typography variant="body2" color="textSecondary" sx={{
                            color: '#FFFFFF',
                            fontWeight: 600,
                            // no wrap
                            whiteSpace: 'nowrap',
                            fontSize: '16px',
                            py: 2,
                            px: 3,
                        }}>
                            {selectedValues.length} sélectionnée{selectedValues.length > 1 ? 's' : ''}
                        </Typography>
                    </Box>
                </FlexRow>
                <Box sx={{ flexGrow: 1 }}>
                    <Grid container sx={{ width: 'calc(100%+16px)', justifyContent: "center" }} spacing={2}>
                        {loading &&
                            Array.from({ length: 6 }).map((_, val) => (
                                <Grid item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    key={val}
                                >
                                    <JobOfferCardSkeleton />
                                </Grid>
                            ))}
                        {!loading && data.length === 0 && (
                            <Grid item xs={12}>
                                <EmptyState
                                    title="Nous n'avons actuellement aucun candidat"
                                    subtitle="Commençons par inviter de nouveaux talents à s'inscrire sur Upercut."
                                />
                            </Grid>
                        )}
                        {!loading &&
                            data?.length > 0 &&
                            data.map(jobOffer => {
                                const isSelected = selectedValues.includes(jobOffer.jobNumber);
                                return (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        key={jobOffer._id}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Box
                                            onClick={() => setSelectedValues(prev => {
                                                if (prev.includes(jobOffer.jobNumber)) {
                                                    return prev.filter(id => id !== jobOffer.jobNumber);
                                                }
                                                return [...prev, jobOffer.jobNumber];
                                            })}
                                            sx={{
                                                outline: isSelected ? theme => `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
                                                borderRadius: '14px',
                                                cursor: 'pointer',
                                                position: 'relative',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}>
                                            <Box
                                                component="i"
                                                className='fi fi-ss-check-circle'
                                                sx={{
                                                    position: 'absolute',
                                                    top: -12,
                                                    left: -12,
                                                    color: theme => theme.palette.primary.main,
                                                    backgroundColor: "#FFF",
                                                    height: '22px',
                                                    width: '22px',
                                                    borderRadius: '50%',
                                                    display: isSelected ? 'flex' : 'none',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '24px',
                                                    zIndex: 3,
                                                }} />
                                            <JobOfferCard
                                                job={jobOffer}
                                                disableLink
                                            />
                                        </Box>
                                    </Grid>
                                );
                            })}
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button
                    variant="contained"
                    className="submit-btn"
                    onClick={handleSubmit}
                    color="primary"
                    size="large"
                    startIcon={isLoadingSubmit ? <CircularProgress size={12} /> : null}
                    disabled={selectedValues.length === 0 || isLoadingSubmit}>
                    Suggérer une offre d&apos;emploi
                </Button>
            </DialogActions>
        </Dialog >
    );
};

export default SuggestJobOfferDialog;
